import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import api from "../axiosConfig";

const CHANNEL_META = {
  email: {
    label: "Email",
    color: "#1565c0",
    bg: "linear-gradient(135deg, rgba(227,242,253,0.94) 0%, rgba(240,247,255,0.72) 100%)",
    icon: <MailOutlineIcon sx={{ fontSize: 18 }} />,
  },
  whatsapp: {
    label: "WhatsApp",
    color: "#128C7E",
    bg: "linear-gradient(135deg, rgba(220,252,231,0.94) 0%, rgba(240,253,244,0.72) 100%)",
    icon: <WhatsAppIcon sx={{ fontSize: 18 }} />,
  },
};

function resolveDigits(value, digits) {
  if (Number.isFinite(Number(digits))) {
    return Number(digits);
  }

  const numeric = Math.abs(Number(value));
  if (!Number.isFinite(numeric) || numeric === 0) {
    return 0;
  }

  if (numeric < 1) {
    return 4;
  }

  return Number.isInteger(numeric) ? 0 : 2;
}

function formatNumber(value, digits) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "0";
  }

  const resolvedDigits = resolveDigits(numeric, digits);

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: resolvedDigits,
    maximumFractionDigits: resolvedDigits,
  }).format(numeric);
}

function formatUsage(used, limit) {
  const numericLimit = Number(limit);
  if (!Number.isFinite(numericLimit) || numericLimit <= 0) {
    return "No cap configured";
  }

  return `${formatNumber(used)} / ${formatNumber(numericLimit)}`;
}

function titleize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
}

function costSummary(channel) {
  const cost = Number(channel?.cost_per_recipient ?? 0);
  const digits = channel?.channel === "whatsapp" ? 4 : 2;

  if (channel?.pricing_model === "template_per_message") {
    const category = titleize(channel?.pricing_category || "template");
    const market = channel?.rate_market || channel?.pricing_market || null;
    const currency = channel?.currency || "USD";

    return `Est. ${category} template rate${market ? ` - ${market}` : ""} - ${currency} ${formatNumber(cost, digits)}`;
  }

  return `Cost / recipient ${formatNumber(cost, digits)}`;
}

function throughputLabel(meta) {
  const throughput = meta?.status?.throughput;
  if (!throughput) {
    return null;
  }

  if (typeof throughput === "string" || typeof throughput === "number") {
    return titleize(String(throughput));
  }

  if (Array.isArray(throughput)) {
    return throughput.map((value) => titleize(String(value))).join(" / ");
  }

  if (typeof throughput === "object") {
    const preferred =
      throughput.level ??
      throughput.value ??
      throughput.name ??
      throughput.status ??
      null;

    if (preferred) {
      return titleize(String(preferred));
    }
  }

  return null;
}

function sourceOfTruthLabel(channel) {
  if (channel?.source_of_truth === "min_manual_meta") {
    return "Using min(manual, Meta)";
  }

  if (channel?.channel === "whatsapp") {
    return "Using manual budget";
  }

  return null;
}

function BudgetLine({ label, used, limit, color }) {
  const numericLimit = Number(limit);
  if (!Number.isFinite(numericLimit) || numericLimit <= 0) {
    return null;
  }

  const numericUsed = Math.max(0, Number(used || 0));
  const progress = Math.min(100, Math.round((numericUsed / numericLimit) * 100));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 900 }}>
          {formatUsage(numericUsed, numericLimit)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: `${color}18`,
          "& .MuiLinearProgress-bar": {
            bgcolor: color,
            borderRadius: 999,
          },
        }}
      />
    </Box>
  );
}

function parseBudgetField(value) {
  const num = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(num) && num >= 0 ? num : null;
}

function BudgetEditDialog({ open, channel, onClose, onSaved }) {
  const channelKey = channel?.channel;
  const [dailyLimit, setDailyLimit] = useState(String(channel?.daily_limit ?? ""));
  const [monthlyLimit, setMonthlyLimit] = useState(String(channel?.monthly_limit ?? ""));
  const [reserveBuffer, setReserveBuffer] = useState(String(channel?.reserve_buffer ?? "0"));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        daily_limit: parseBudgetField(dailyLimit),
        monthly_limit: parseBudgetField(monthlyLimit),
        reserve_buffer: parseBudgetField(reserveBuffer) ?? 0,
      };
      await api.put(`/analytics/marketing/broadcast-budget/${channelKey}`, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save budget.");
    } finally {
      setSaving(false);
    }
  };

  const label = CHANNEL_META[channelKey]?.label || channelKey;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust {label} Budget</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            These limits are enforced globally across all users and campaigns. Leave a field empty to remove its cap.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Daily limit"
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            type="number"
            inputProps={{ min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">msgs</InputAdornment> }}
            helperText="Max messages per day (all users combined)"
            fullWidth
          />
          <TextField
            label="Monthly limit"
            value={monthlyLimit}
            onChange={(e) => setMonthlyLimit(e.target.value)}
            type="number"
            inputProps={{ min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">msgs</InputAdornment> }}
            helperText="Max messages per month (all users combined)"
            fullWidth
          />
          <TextField
            label="Reserve buffer"
            value={reserveBuffer}
            onChange={(e) => setReserveBuffer(e.target.value)}
            type="number"
            inputProps={{ min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">msgs</InputAdornment> }}
            helperText="Held back from the cap as a safety buffer"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Save Budget
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BudgetCard({ channel, isAdmin, onSaved }) {
  const [editOpen, setEditOpen] = useState(false);
  const meta = CHANNEL_META[channel?.channel] || {};
  const color = meta.color || "#2db838";
  const bg = meta.bg || "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.82) 100%)";
  const icon = meta.icon || null;
  const label = channel?.label || meta.label || String(channel?.channel || "Channel");
  const remaining = channel?.remaining === null || channel?.remaining === undefined ? null : Number(channel.remaining);
  const unlimited = remaining === null || !Number.isFinite(remaining);
  const dailyLimit = Number.isFinite(Number(channel?.daily_limit)) ? Number(channel.daily_limit) : null;
  const monthlyLimit = Number.isFinite(Number(channel?.monthly_limit)) ? Number(channel.monthly_limit) : null;
  const remainingLabel = unlimited ? "No cap" : formatNumber(remaining);
  const badgeLabel = unlimited ? "No cap set" : `${formatNumber(remaining)} left`;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        p: 2.25,
        borderRadius: 4,
        overflow: "hidden",
        position: "relative",
        background: bg,
        border: `1px solid ${color}18`,
        boxShadow: `0 20px 34px -28px ${color}55`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 auto auto 0",
          width: "100%",
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${color}55 100%)`,
        }}
      />

      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar sx={{ bgcolor: `${color}14`, color, width: 42, height: 42 }}>
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={950} noWrap>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {costSummary(channel)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            size="small"
            label={badgeLabel}
            sx={{
              fontWeight: 800,
              color: unlimited || remaining > 0 ? color : "#b91c1c",
              bgcolor: unlimited ? `${color}10` : remaining > 0 ? `${color}10` : "rgba(239,68,68,0.10)",
              border: `1px solid ${unlimited || remaining > 0 ? `${color}22` : "rgba(239,68,68,0.22)"}`,
            }}
          />
          {isAdmin ? (
            <Tooltip title="Adjust budget">
              <IconButton size="small" onClick={() => setEditOpen(true)} sx={{ color }}>
                <EditRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      <Box sx={{ mt: 1.6 }}>
        <Typography variant="h4" fontWeight={950} sx={{ lineHeight: 1 }}>
          {remainingLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          {unlimited ? "Configure a shared broadcast budget to enforce this guard." : "Remaining broadcast budget"}
        </Typography>
      </Box>

      <Stack spacing={1.15} sx={{ mt: 1.5 }}>
        <BudgetLine label="Daily" used={channel?.daily_used} limit={dailyLimit} color={color} />
        {monthlyLimit !== null ? (
          <BudgetLine label="Monthly" used={channel?.monthly_used} limit={monthlyLimit} color={color} />
        ) : (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>Monthly</Typography>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>No cap</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={0} sx={{ height: 8, borderRadius: 999, bgcolor: `${color}18`, "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 999 } }} />
          </Box>
        )}
        {dailyLimit === null && monthlyLimit === null ? (
          <Chip variant="outlined" label="No cap configured" sx={{ alignSelf: "flex-start", fontWeight: 700, borderRadius: 999 }} />
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: "auto", pt: 1.5 }}>
        <Chip
          size="small"
          variant="outlined"
          label={unlimited ? "Guard waiting for a cap" : "Guard enforced globally"}
          sx={{ borderRadius: 999, fontWeight: 700 }}
        />
      </Stack>

      {isAdmin ? (
        <BudgetEditDialog
          open={editOpen}
          channel={channel}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onSaved?.(); }}
        />
      ) : null}
    </Paper>
  );
}

export default function BroadcastBudgetPanel({
  title = "Broadcast Budget",
  subtitle = "Live remaining budget and spend guard by channel.",
  channels = [],
  loading = false,
  actions = null,
  isAdmin = false,
  onBudgetSaved,
  sx,
}) {
  const items = Array.isArray(channels) ? channels : [];
  const scopeLabel =
    items
      .map((channel) => channel?.label)
      .filter(Boolean)
      .join(" + ") || "Broadcast channels";

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        ...sx,
      }}
    >
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.8, md: 2.1 } }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Box>
            <Typography variant="h6" fontWeight={900}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {actions}
            <Chip
              label={scopeLabel}
              color="success"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 700 }}
            />
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 } }}>
        {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 999 }} /> : null}

        {items.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: items.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))" },
              gap: 2,
              alignItems: "stretch",
            }}
          >
            {items.map((channel) => (
              <Box key={channel?.channel || channel?.label} sx={{ minWidth: 0, height: "100%" }}>
                <BudgetCard channel={channel} isAdmin={isAdmin} onSaved={onBudgetSaved} />
              </Box>
            ))}
          </Box>
        ) : loading ? (
          <Box
            sx={{
              border: "1px dashed rgba(15, 23, 42, 0.12)",
              borderRadius: 3,
              px: 2,
              py: 3,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Loading budget snapshot...
            </Typography>
            <Typography variant="caption">
              We are reading the current email and WhatsApp caps.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px dashed rgba(15, 23, 42, 0.12)",
              borderRadius: 3,
              px: 2,
              py: 3,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Budget status unavailable.
            </Typography>
            <Typography variant="caption">
              We could not load the broadcast budget snapshot right now.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
